import type { DividerPolicy, LoaderMode } from "@CommonTypes/backend/PspRules";
import type { PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import type { ShelfLoaderDecision } from "@CommonTypes/merchflow/pog/shelf";
import {
  getBaseLoaderEvidence,
  loaderModeOf,
  resolveLoaderPolicy,
  summarizeBaseLoaders,
  type BaseLoaderEvidence,
  type BaseLoaderSummary,
  type DividerPolicyProduct,
  type LoaderLibraryShelf,
} from "@CommonUtils/pog/dividerPolicy";
import type { DividerRuleError } from "@CommonUtils/pog/dividerPolicyValidation";
import { translate } from "src/i18n/translate";
import { displayLength, formatLength, formatLengthError } from "src/utils/lengthUnit";

/** The loader half of the Dividers section: the base evidence, the resolved ticks and the shelves. */
export interface LoaderRulesState {
  evidence: BaseLoaderEvidence;
  summary: BaseLoaderSummary;
  /** Product codes that get a spring loader under the rule (the second tick column). */
  ticked: Set<string>;
  /** "products" ("Follow the products", also when absent) or "shelf" ("Stay with the shelf"). */
  mode: LoaderMode;
}

/**
 * Computed like the backend computes it (DEV-3518): the base store's Fixtures
 * page rows act as the base shelves' hand settings.
 */
export const loaderRulesState = (
  policy: DividerPolicy,
  products: readonly DividerPolicyProduct[],
  basePog: PogNonCircular | null,
  libraryShelves: readonly LoaderLibraryShelf[] = [],
): LoaderRulesState => {
  const evidence = getBaseLoaderEvidence(basePog, libraryShelves);
  const loaders = resolveLoaderPolicy({ policy, products, evidence });
  const ticked = new Set(
    [...loaders.needs].filter(([, need]) => need.needed).map(([code]) => code),
  );
  return {
    evidence,
    summary: summarizeBaseLoaders({ evidence, loaders }),
    ticked,
    mode: loaders.mode,
  };
};

/** The "Spring loaders" choice's stored values, in order; the section words them. */
export const LOADER_MODES: readonly LoaderMode[] = ["products", "shelf"];

/** The rule with the choice written; written whenever the user picks an option. */
export const withLoaderMode = (policy: DividerPolicy, mode: LoaderMode): DividerPolicy => ({
  ...policy,
  loaderMode: mode,
});

/** The rule's stored choice, carried through the tick and size edits (absent stays absent). */
export const keptLoaderMode = (policy: DividerPolicy): Pick<DividerPolicy, "loaderMode"> =>
  policy.loaderMode === undefined ? {} : { loaderMode: loaderModeOf(policy) };

/** "1.5 in deep" or "1.5 to 2 in deep"; null when no depth is known. */
const describeDepths = (depths: readonly number[], unit: string) => {
  if (depths.length === 0) return null;
  const low = Math.min(...depths);
  const high = Math.max(...depths);
  return low === high
    ? translate("common", "planogramRules.dividers.loaderO.depth", {
        depth: formatLength(low, unit),
      })
    : translate("common", "planogramRules.dividers.loaderO.depthRange", {
        low: displayLength(low),
        high: formatLength(high, unit),
      });
};

/**
 * O's sentence about spring loaders (review B.1), after the dividers one.
 * Null when the base planogram could not be read and the rule follows it: the
 * dividers sentence already says so.
 */
export const describeLoadersForO = ({
  policy,
  basePog,
  products,
  state,
  unit,
}: {
  policy: DividerPolicy;
  basePog: PogNonCircular | null;
  products: readonly DividerPolicyProduct[];
  state: LoaderRulesState;
  unit: string;
}): string | null => {
  const followsBase = policy.source === "base";
  const { evidence, summary, ticked, mode } = state;
  // "Stay with the shelf" always reads the base planogram's shelves.
  if (!basePog && (followsBase || policy.source === "none" || mode === "shelf")) return null;
  if (mode === "shelf") return describeShelfModeLoaders(followsBase, summary, unit);
  if (followsBase && evidence.loaderShelves === 0) {
    return translate("common", "planogramRules.dividers.loaderO.noneInBase");
  }
  if (ticked.size === 0 && summary.loaderShelves === 0) {
    return translate("common", "planogramRules.dividers.loaderO.none");
  }

  const productCount = translate("common", "planogramRules.dividers.o.productCount", {
    count: ticked.size,
    total: products.length,
  });
  // Shelves due a loader, with or without a depth: "depth not set on {k} of them".
  const shelfCount = translate("common", "planogramRules.dividers.o.shelfCount", {
    count: summary.loaderShelves + summary.depthNotSet,
    total: summary.shelves,
  });
  const depth =
    describeDepths(summary.depths, unit) ??
    (policy.loaderDepth ? describeDepths([policy.loaderDepth], unit) : null);
  const params = { products: productCount, shelves: shelfCount, depth: depth ?? "" };
  if (followsBase) {
    if (summary.depthNotSet > 0) {
      return translate("common", "planogramRules.dividers.loaderO.likeBaseDepthNotSet", {
        ...params,
        count: summary.depthNotSet,
      });
    }
    return translate(
      "common",
      depth
        ? "planogramRules.dividers.loaderO.likeBaseWithDepth"
        : "planogramRules.dividers.loaderO.likeBase",
      params,
    );
  }
  if (summary.depthNotSet > 0) {
    return translate("common", "planogramRules.dividers.loaderO.noDepthYet", params);
  }
  return translate(
    "common",
    depth
      ? "planogramRules.dividers.loaderO.forProductsWithDepth"
      : "planogramRules.dividers.loaderO.forProducts",
    params,
  );
};

/**
 * "Stay with the shelf": the shelves whose hardware has a loader keep it,
 * whatever lands there, so the sentence counts shelves, not products, and {d}
 * is the hardware's own depth (never "depth not set").
 */
const describeShelfModeLoaders = (
  followsBase: boolean,
  summary: BaseLoaderSummary,
  unit: string,
) => {
  const kept = summary.loaderShelves;
  if (kept === 0) return translate("common", "planogramRules.dividers.loaderO.shelfModeNone");
  const depth = describeDepths(summary.depths, unit);
  if (followsBase) {
    const shelves = translate(
      "common",
      kept === 1 ? "planogramRules.dividers.o.oneShelf" : "planogramRules.dividers.o.shelves",
      { count: kept },
    );
    return depth
      ? translate("common", "planogramRules.dividers.loaderO.shelfModeBaseWithDepth", {
          shelves,
          depth,
        })
      : translate("common", "planogramRules.dividers.loaderO.shelfModeBase", { shelves });
  }
  const counts = { kept, total: summary.shelves };
  return depth
    ? translate("common", "planogramRules.dividers.loaderO.shelfModeSomeWithDepth", {
        ...counts,
        depth,
      })
    : translate("common", "planogramRules.dividers.loaderO.shelfModeSome", counts);
};

/** The flow depth in use for the sizes row: the rule's own, else the base's most common. */
export const loaderDepthInUse = (policy: DividerPolicy, evidence: BaseLoaderEvidence) =>
  policy.loaderDepth && policy.loaderDepth > 0 ? policy.loaderDepth : evidence.typicalDepth;

/**
 * The policy with the depth the sizes row shows stored as the flow depth, when
 * it has none of its own and the base prefills one. Applied on the first tick
 * edit: a picked list can bring a loader to a shelf with no depth of its own,
 * and the backend reads only the stored `loaderDepth` for it.
 */
export const withPrefilledLoaderDepth = (
  policy: DividerPolicy,
  evidence: BaseLoaderEvidence,
): DividerPolicy =>
  (policy.loaderDepth ?? 0) > 0 || !(evidence.typicalDepth && evidence.typicalDepth > 0)
    ? policy
    : { ...policy, loaderDepth: evidence.typicalDepth };

/** The sizes row's note under the depth: where the prefilled depth comes from. */
export const loaderDepthNoteSource = (policy: DividerPolicy, evidence: BaseLoaderEvidence) =>
  (policy.loaderDepth ?? 0) > 0 || evidence.typicalDepth === null ? null : evidence.depthSource;

/**
 * Why the loader half cannot be generated with as it stands, or null: the
 * depth is required only when a shelf is due a loader and no depth is known
 * for it; a flow depth must stay below the shallowest base shelf.
 */
export const loaderDepthError = (
  policy: DividerPolicy,
  state: LoaderRulesState,
): Pick<DividerRuleError, "code" | "params"> | null => {
  // "Stay with the shelf" uses each shelf's own hardware depth, never the flow's.
  if (state.mode === "shelf") return null;
  const depth = policy.loaderDepth ?? 0;
  const shallowest = state.evidence.shallowestShelfDepth;
  if (depth > 0 && shallowest !== null && shallowest > 0 && depth >= shallowest) {
    return { code: "LOADER_DEPTH_NOT_BELOW_SHALLOWEST_SHELF", params: { depth: shallowest } };
  }
  if (state.summary.depthNotSet > 0) return { code: "LOADER_DEPTH_MISSING" };
  return null;
};

/** `loaderDepthError` in words. */
export const loaderDepthProblem = (
  policy: DividerPolicy,
  state: LoaderRulesState,
  unit: string,
): string | null => {
  const error = loaderDepthError(policy, state);
  return error && formatLengthError(error, unit);
};

/**
 * The base shelves as the loader evidence numbers them (per bay, top first by
 * notch, stored order breaking ties), keyed by stored position, so a result
 * row finds its own shelf's decision whatever order the shelves are stored in.
 */
export const loaderDecisionsByStoredShelf = (
  basePog: PogNonCircular | null,
  summary: BaseLoaderSummary,
): Map<string, ShelfLoaderDecision> => {
  const byLoaderKey = new Map(
    summary.rows.map((row) => [`${row.bayNo}:${row.shelfNo}`, row.decision]),
  );
  const result = new Map<string, ShelfLoaderDecision>();
  (basePog?.planogram?.bays ?? []).forEach((bay, bayIndex) => {
    (bay.shelves ?? [])
      .map((shelf, index) => ({ notch: Number(shelf.notchNo) || 0, index }))
      .sort((a, b) => b.notch - a.notch || a.index - b.index)
      .forEach(({ index }, topFirst) => {
        const decision = byLoaderKey.get(`${bayIndex + 1}:${topFirst + 1}`);
        if (decision) result.set(`${bayIndex + 1}:${index + 1}`, decision);
      });
  });
  return result;
};

/**
 * The loader word of a result row: "loader 1.5 in" (follows the products, at
 * that depth) or "no loader". Under "Stay with the shelf" every loader is the
 * shelf's own, fixed: "loader 1.5 in (shelf)".
 */
export const describeRowLoader = (
  decision: ShelfLoaderDecision | undefined,
  unit: string,
  mode: LoaderMode = "products",
) => {
  if (!decision?.depth) return translate("common", "planogramRules.dividers.result.noLoader");
  return translate(
    "common",
    mode === "shelf"
      ? "planogramRules.dividers.result.loaderOfShelf"
      : "planogramRules.dividers.result.loader",
    { depth: formatLength(decision.depth, unit) },
  );
};

export const describeRowLoaderWithCount = (
  decision: ShelfLoaderDecision | undefined,
  unit: string,
  mode: LoaderMode = "products",
) => {
  const word = describeRowLoader(decision, unit, mode);
  if (!decision) return word;
  const count = translate(
    "common",
    decision.reason === "needs-more-than-half"
      ? "planogramRules.dividers.result.countNeedsMoreThanHalf"
      : "planogramRules.dividers.result.count",
    { needing: decision.needing, facings: decision.facings },
  );
  return `${word}, ${count}`;
};

export const loadersInPlay = (state: LoaderRulesState) =>
  state.evidence.loaderShelves > 0 || state.ticked.size > 0 || state.summary.loaderShelves > 0;
